import {
  BalanceDomain,
  DOMAIN_TO_PARENT_ID_FIELD_NAME,
  DOMAINE_ID_SEPARATOR,
} from "../../utils/constants/balances";
import { TenantStatus } from "../../models/tenant_old.js";
import useTenants from "../../hooks/use-tenants";
import usePropertyFolders from "../../hooks/use-property-folders";
import useProperties from "../../hooks/use-properties";
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
import { getIn, useFormikContext } from "formik";
import {
  formatBalanceAffectation,
  getBalanceParentId,
  getParentIdPropertyFromDomain,
  isAffectableFolder,
  isAffectableTenant,
} from "../../utils/balances";
import { ReactSelect } from "../UI/Select";
import { stripEnd } from "../../utils/index.jsx";
import {
  buildPropertyFoldersTree,
  findPropertyFolder,
  getIncludedPropertyAndFolderIds,
} from "../../utils/properties";

function tenantToOption(tenant, property, disabledOptions) {
  let label = tenant.nom;
  if (property) {
    label += ` (${property.nom})`;
  }
  const value = `${BalanceDomain.TENANT}${DOMAINE_ID_SEPARATOR}${tenant.id}`;
  return {
    label,
    value,
    isDisabled: disabledOptions.includes(value),
  };
}

function propOrFolderToOption(propOrFolder, domaine, disabledOptions) {
  const value = `${domaine}${DOMAINE_ID_SEPARATOR}${propOrFolder.id}`;
  return {
    label: propOrFolder.nom,
    value,
    isDisabled: disabledOptions.includes(value),
  };
}

function profileToOption(profile, disabledOptions) {
  const value = `${BalanceDomain.PROFILE_BAILLEUR}${DOMAINE_ID_SEPARATOR}${profile.id}`;
  return {
    label: profile.nom_profile,
    value,
    isDisabled: disabledOptions.includes(value),
  };
}

function getAffectationValue(balance) {
  return formatBalanceAffectation(balance.domaine, getBalanceParentId(balance));
}

const DEFAULT_DOMAINS = [
  BalanceDomain.FOLDER,
  BalanceDomain.PROPERTY,
  BalanceDomain.TENANT,
];

const EMPTY_IDS = new Set();

export default function AffectationSelect({
  prefix = "",
  parentId,
  domaineBalance,
  subtree,
  className,
  disabled,
  disabledOptions = [],
  mandatory,
  label = "Affectation",
  placeholder = "Chambre 10 ou Jean Dupond",
  withFolders = false,
  allowedDomains = DEFAULT_DOMAINS,
  resetTypeOnChange = true,
  onChange,
}) {
  const { values, setFieldValue } = useFormikContext();
  const { tenants } = useTenants();
  const { propertyFolders } = usePropertyFolders();
  const { properties, propertiesById } = useProperties({ withById: true });
  const { profileBailleurs } = useProfileBailleurs();
  const name = `${prefix}affectation`;
  const balance = getIn(values, stripEnd(prefix, "."));

  // Sans `subtree`, chaque liste conserve tous ses éléments.
  const keep = (ids) => (entity) => !subtree || ids.has(entity.id);

  let propFolderOptions = [];
  let propOptions = [];
  let activeTenantOptions = [];
  let archivedTenantOptions = [];
  let profileOptions = [];
  if (!domaineBalance) {
    propFolderOptions = propertyFolders
      .filter((folder) => isAffectableFolder(folder, withFolders))
      .filter(keep(subtree?.folderIds ?? EMPTY_IDS))
      .map((folder) =>
        propOrFolderToOption(folder, BalanceDomain.FOLDER, disabledOptions),
      );
    propOptions = properties
      .filter(keep(subtree?.propertyIds ?? EMPTY_IDS))
      .map((prop) =>
        propOrFolderToOption(prop, BalanceDomain.PROPERTY, disabledOptions),
      );
    const affectableTenants = tenants
      .filter(isAffectableTenant)
      .filter(keep(subtree?.tenantIds ?? EMPTY_IDS));
    activeTenantOptions = affectableTenants
      .filter((tenant) => tenant.status !== TenantStatus.Archive.status)
      .map((tenant) =>
        tenantToOption(tenant, propertiesById[tenant.id_bien], disabledOptions),
      );
    archivedTenantOptions = affectableTenants
      .filter((tenant) => tenant.status === TenantStatus.Archive.status)
      .map((tenant) =>
        tenantToOption(tenant, propertiesById[tenant.id_bien], disabledOptions),
      );
    profileOptions = profileBailleurs
      .filter(keep(subtree?.profileIds ?? EMPTY_IDS))
      .map((profile) => profileToOption(profile, disabledOptions));
  } else if (domaineBalance === BalanceDomain.FOLDER) {
    const propertyFoldersTree = buildPropertyFoldersTree(
      propertyFolders,
      properties,
    );
    const { propertyIds, folderIds } = getIncludedPropertyAndFolderIds(
      findPropertyFolder(propertyFoldersTree.property_folders, parentId),
    );
    propFolderOptions = propertyFolders
      .filter((pf) => folderIds.has(pf.id) && pf.id !== parentId)
      .map((folder) =>
        propOrFolderToOption(folder, BalanceDomain.FOLDER, disabledOptions),
      );
    propOptions = properties
      .filter((p) => propertyIds.has(p.id))
      .map((prop) =>
        propOrFolderToOption(prop, BalanceDomain.PROPERTY, disabledOptions),
      );
  }

  const groupsByDomain = {
    [BalanceDomain.FOLDER]: [{ label: "Dossiers", options: propFolderOptions }],
    [BalanceDomain.PROPERTY]: [{ label: "Biens", options: propOptions }],
    [BalanceDomain.TENANT]: [
      { label: "Locataires Actifs", options: activeTenantOptions },
      { label: "Locataires Archivés", options: archivedTenantOptions },
    ],
    [BalanceDomain.PROFILE_BAILLEUR]: [
      { label: "Profils", options: profileOptions },
    ],
  };
  const affectationSelectOptions = allowedDomains.flatMap(
    (domain) => groupsByDomain[domain] ?? [],
  );

  function affectationChangeHandler(prefix, option, domaineBalance) {
    if (option == null) return;

    const promises = [];
    const [newDomaineBalance, parentId] =
      option.value.split(DOMAINE_ID_SEPARATOR);
    if (onChange) {
      onChange(newDomaineBalance, parentId);
    }
    if (newDomaineBalance !== domaineBalance) {
      if (resetTypeOnChange) {
        promises.push(setFieldValue(`${prefix}type`, ""));
      }
      promises.push(setFieldValue(`${prefix}domaine`, newDomaineBalance));
    }
    // Reset all parent IDs and set the new one
    for (let domain in DOMAIN_TO_PARENT_ID_FIELD_NAME) {
      promises.push(
        setFieldValue(`${prefix}${DOMAIN_TO_PARENT_ID_FIELD_NAME[domain]}`, ""),
      );
    }
    promises.push(
      setFieldValue(
        `${prefix}${getParentIdPropertyFromDomain(newDomaineBalance)}`,
        parentId,
      ),
    );
    return Promise.all(promises);
  }

  return (
    <ReactSelect
      label={label}
      name={name}
      placeholder={placeholder}
      value={getAffectationValue(balance)}
      options={affectationSelectOptions}
      onChange={(option) =>
        affectationChangeHandler(prefix, option, balance.domaine)
      }
      isClearable={false}
      isDisabled={disabled}
      className={className}
      mandatory={mandatory}
    />
  );
}
